import { useFirebase } from '../../../contexts/FirebaseContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { pick } from 'lodash/fp';
import { formatSecondsTimestamp, stableSort } from '../../utils/utils';
import { Copyable } from '../../components/Copyable/Copyable';
import { UserUID } from '../../components/UserUID/UserUID';
import { Badge, Button, Card, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './Realtime.scss';
import { faAngleDoubleUp, faSyncAlt, faVideo, faVideoSlash } from '@fortawesome/free-solid-svg-icons';
import { Title } from '../../components/Title/Title';
import { Topbar } from '../../components/Topbar/Topbar';
import { Chart } from 'react-charts';
import { BetaBadge } from '../../components/BetaBadge/BetaBadge';

const RealtimeComponent = () => {
  const { realtimeDatabase, requestVideoData } = useFirebase();
  const [data, setData] = useState<any[]>([]);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoLoadingGuid, setVideoLoadingGuid] = useState('');

  const [currentlyWatching, setCurrentlyWatching] = useState(0);

  const startTime = useRef(Date.now());
  const videoDataMap = useRef(new Map());

  const previousChartData = useRef([]);
  const chartData = useMemo(() => {
    const newData = [...previousChartData.current, [Date.now(), currentlyWatching]];
    previousChartData.current = newData;
    return [
      {
        label: 'Visioni',
        data: newData,
      },
    ];
  }, [currentlyWatching]);
  const axes = useMemo(
    () => [
      { primary: true, type: 'time', position: 'bottom' },
      { type: 'ordinal', position: 'left' },
    ],
    []
  );
  const tooltip = useMemo(
    () => ({
      align: 'closest',
      anchor: 'auto',
    }),
    []
  );

  useEffect(() => {
    const picker = pick(['event', 'uid', 'id', 'watching', 'video', 'name', 'title', 'ts', 'ua']);

    realtimeDatabase.goOnline();
    const ref = realtimeDatabase.ref(`users`);

    const callback = (event) => (snap) => {
      const snapData = snap.val() || {};
      if (snapData.watching == null || snapData.ts == null) return;
      if (snapData.ts < startTime.current && !snapData.watching) return;
      const newItem = { ...snapData, uid: snap.key, event };
      setData((current) => stableSort([...current, picker(newItem)], (a, b) => a.ts - b.ts));
      if (newItem.watching) setCurrentlyWatching((current) => current + 1);
      else setCurrentlyWatching((current) => current - 1);
    };

    ref.on('child_added', callback('CHILD_ADDED'));
    ref.on('child_changed', callback('CHILD_CHANGED'));

    return () => {
      ref.off();
      realtimeDatabase.goOffline();
    };
  }, []);

  const getVideoInfo = (guid) => {
    const cachedData = videoDataMap.current.get(guid);
    if (cachedData) {
      return (
        <>
          <strong>{cachedData.title}</strong>
        </>
      );
    }

    return (
      <>
        <Copyable text={guid} />
        {!!guid && (
          <Button
            as={Badge}
            size={'sm'}
            variant={'outline-primary'}
            className={'ml-2'}
            disabled={videoLoading}
            onClick={() => {
              if (videoLoading) return;
              getVideoData(guid);
            }}
          >
            {videoLoadingGuid == guid ? 'Caricamento...' : 'Ottieni dati video'}
          </Button>
        )}
      </>
    );
  };

  const getVideoInfoShort = (guid) => {
    const cachedData = videoDataMap.current.get(guid);
    if (cachedData) {
      return (
        <>
          <Copyable text={guid} />
          {/*<Copyable text={cachedData.filename} />*/}
        </>
      );
    }

    return null;
  };

  const getVideoData = (guid) => {
    if (videoLoading) return;

    setVideoLoading(true);
    setVideoLoadingGuid(guid);
    requestVideoData(guid, null)
      .then((res) => {
        videoDataMap.current.set(guid, res);
      })
      .catch(console.error)
      .finally(() => {
        setVideoLoading(false);
        setVideoLoadingGuid('');
      });
  };

  return (
    <>
      <Title>RWS</Title>

      <div style={{ marginBottom: '72px' }}>
        <div style={{ backgroundColor: '#141414', position: 'fixed', top: '0', left: '0', right: '0', zIndex: 100 }}>
          <Topbar />
        </div>
      </div>

      <Container className={'my-5'}>
        <div className={'display-4 mb-5 text-center'}>
          Realtime Watch Stream
          <BetaBadge big />
        </div>

        <div className="mb-5" style={{ height: '200px' }}>
          <Chart data={chartData} axes={axes} dark tooltip={tooltip} primaryCursor secondaryCursor />
        </div>

        <div className={'realtime-card-container'}>
          <div className={'d-flex align-items-center realtime-card-wrapper'}>
            <div className={'realtime-number-circle last'}>
              <FontAwesomeIcon icon={faAngleDoubleUp} />
            </div>
            <Card bg={'dark'} className={'mb-3 flex-grow-1'}>
              <Card.Body>
                <div className={'small text-muted'}>{formatSecondsTimestamp(startTime.current / 1000, 'dd/MM/Y HH:mm:ss')}</div>
                <div>Watch stream iniziato.</div>
              </Card.Body>
            </Card>
          </div>

          {data.map((item, i) => (
            <div className={'d-flex align-items-center realtime-card-wrapper'}>
              <div className={'realtime-number-circle'}>{i + 1}</div>
              <Card bg={'dark'} key={`${item.ts}-${item.uid}`} className={'mb-3 flex-grow-1'}>
                <Card.Body>
                  <div className={'small text-muted'}>
                    <FontAwesomeIcon icon={item.watching ? faVideo : faVideoSlash} className={'mr-2 ' + (item.watching ? 'text-primary' : 'text-secondary')} />
                    {formatSecondsTimestamp(item.ts / 1000, 'dd/MM/Y HH:mm:ss')}{' '}
                    {!!item.ua && (
                      <>
                        <Badge variant={'dark'} style={{ fontSize: '0.7rem' }}>
                          {item.ua.browser} su {item.ua.os}
                        </Badge>
                      </>
                    )}{' '}
                    {getVideoInfoShort(item.video)}
                  </div>
                  {item.watching ? (
                    <div>
                      <UserUID uid={item.uid} customText={item.name} /> <span>sta guardando</span> {getVideoInfo(item.video)}
                    </div>
                  ) : (
                    <div>
                      <UserUID uid={item.uid} customText={item.name} /> ha interrotto la visione.
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          ))}

          <div className={'d-flex align-items-center realtime-card-wrapper'}>
            <div className={'realtime-number-circle first'}>
              <FontAwesomeIcon icon={faSyncAlt} spin />
            </div>
            <Card bg={'dark'} className={'mb-3 flex-grow-1'}>
              <Card.Body>
                <div>In ascolto per nuovi eventi&hellip;</div>
                <div className={'small text-muted'}>I nuovi eventi verrano visualizzati di seguito, in ordine cronologico inverso.</div>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/*<pre>{JSON.stringify(data, null, 2)}</pre>*/}
      </Container>
    </>
  );
};

export default RealtimeComponent;
